import React, { memo, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import css from 'styled-jsx/css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle,
  faCircleNotch,
  faFileInvoice,
  faInfo,
  faTag,
} from '@fortawesome/free-solid-svg-icons';

import { ORGANIZATION } from '../../Constants/RBAC';
import { hasWriteAccessToResource } from '../../RBAC/AccessTools';
import AccountService from '../../Services/AccountService';
import { handleImageFileConversion } from '../../Lib/FileHelpers';

import OrgBilling from './OrgBilling';
import OrgBranding from './OrgBranding';
import OrgContactInfo from './OrgContactInfo';
import OrgFeatures from './OrgFeatures';

export default memo(EditOrgModal);

const LOGO_ERROR_MSG = 'Error. Unknown file type.';

const { className: modalClass, styles: modalStyles } = css.resolve`
  @media (max-width: 991px) {
    .modal-dialog {
      height: 100%;
      width: 100%;
      max-height: 100%;
      max-width: 100%;
      margin: 0;
    }
  }
`;

const { className: contentClass, styles: contentStyles } = css.resolve`
  @media (max-width: 767px) {
    .modal-content {
      display: grid;
      grid-template-rows: 60px auto 60px;
    }
  }
  @media (max-width: 991px) {
    .modal-content {
      height: 100%;
    }
  }
`;

const { className: modalBodyClass, styles: modalBodyStyles } = css.resolve`
  .modal-body {
    padding: 0;
    overflow: auto;
  }
  @media (min-width: 768px) {
    .modal-body {
      border-bottom-left-radius: 1rem;
      border-bottom-right-radius: 1rem;
    }
  }
`;

const initState = {
  error: '',
  org: {},
  updateRequestPending: false,
  viewing: 'details',
};

function EditOrgModal(props) {
  const [state, updateState] = useState(() => {
    const propsToInclude = [
      'account_id',
      'account_type',
      'city',
      'logo',
      'name',
      'phoneNumber',
      'settings',
      'street1',
      'street2',
      'state',
      'subscription_terms',
      'zip',
    ];
    const org = Object.entries(props?.org ?? {}).reduce((acc, [key, prop]) => {
      if (!prop || !propsToInclude.includes(key)) return acc;
      acc[key] = prop;
      return acc;
    }, {});
    const {
      billing: { enabled = false } = {},
      csvExport: { signals } = {},
      cumminsenabled = false,
      maintenance_v1: { enabled: maintenance_v1 = false } = {},
      maintenance_v2: { enabled: maintenance_v2 = false } = {},
      parts = false,
      remote = false,
    } = props.org?.features || {};
    return {
      ...initState,
      billingEnabled: enabled,
      csvExport: !!signals,
      cumminsenabled,
      maintenance_v1,
      maintenance_v2,
      org,
      parts,
      remote,
    };
  });

  const hasBilling = useMemo(
      () => props.currentOrg?.features?.billing?.enabled ?? false,
      [props.currentOrg?.features?.billing?.enabled]
    ),
    hasCsvReports = useMemo(
      () => !!props.currentOrg?.features?.csvExport?.signals,
      [props.currentOrg?.features?.csvExport?.signals]
    ),
    hasCumminsenabled = useMemo(
      () => props.currentOrg?.features?.cumminsenabled ?? false,
      [props.currentOrg?.features?.cumminsenabled]
    ),
    hasMaintenanceV1 = useMemo(
      () => props.currentOrg?.features?.maintenance_v1?.enabled ?? false,
      [props.currentOrg?.features?.maintenance_v1?.enabled]
    ),
    hasMaintenanceV2 = useMemo(
      () => props.currentOrg?.features?.maintenance_v2?.enabled ?? false,
      [props.currentOrg?.features?.maintenance_v2?.enabled]
    ),
    hasParts = useMemo(
      () => props.currentOrg?.features?.parts ?? false,
      [props.currentOrg?.features?.parts]
    ),
    hasRemote = useMemo(
      () => props.currentOrg?.features?.remote ?? false,
      [props.currentOrg?.features?.remote]
    );
  const featuresAvailable = useMemo(() => {
    return (
      hasBilling ||
      hasCsvReports ||
      hasMaintenanceV1 ||
      hasMaintenanceV2 ||
      hasParts ||
      hasRemote
    );
  }, [
    hasBilling,
    hasCsvReports,
    hasMaintenanceV1,
    hasMaintenanceV2,
    hasParts,
    hasRemote,
  ]);
  const accountService = useMemo(() => new AccountService(), []);

  const handleFeaturesUpdate = ({ target: { name, value } }) => {
    updateState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handlePhoneNumberUpdate = ({ target: { name, value } }) => {
    const rawNumber = value.replace(/\D/g, '');
    updateState((prevState) => ({
      ...prevState,
      org: { ...prevState.org, [name]: rawNumber },
    }));
  };

  const handlePropUpdate = ({ target: { name, value } }) => {
    updateState((prevState) => ({
      ...prevState,
      org: { ...prevState.org, [name]: value },
    }));
  };

  const handleSettingsUpdate = ({ target: { name, value } }) => {
    updateState((prevState) => ({
      ...prevState,
      org: {
        ...prevState.org,
        settings: { ...(prevState.org?.settings || {}), [name]: value },
      },
    }));
  };

  const onDrop = ([file]) => {
    try {
      const handleError = () =>
        updateState((prevState) => ({ ...prevState, error: LOGO_ERROR_MSG }));
      const handleSuccess = (logo) => {
        updateState((prevState) => ({
          ...prevState,
          error: '',
          org: { ...state.org, logo },
        }));
      };
      handleImageFileConversion(file, handleError, handleSuccess);
    } catch (error) {
      updateState((prevState) => ({ ...prevState, error: LOGO_ERROR_MSG }));
    }
  };

  const submitUpdate = async (e) => {
    e.preventDefault();
    try {
      updateState((prevState) => ({
        ...prevState,
        updateRequestPending: true,
      }));
      const apps = !state.cumminsenabled
        ? props.org.features.apps?.filter((a) => a !== 'engineanalysis') || [] // if not cumminsenabled, make sure engineanalysis is not in apps
        : !Array.isArray(props.org.features.apps) // if enabling for the first time then apps may not be defined if org does not have waterpump app either
          ? ['engineanalysis']
          : !props.org.features.apps.includes('engineanalysis') // if enabling and apps already exists, first check if it already has engineanalysis to avoid duplicates
            ? props.org.features.apps.concat('engineanalysis')
            : props.org.features.apps;
      const billing = props.org.features.billing
        ? { billing: props.org.features.billing }
        : !hasBilling
          ? {}
          : state.billingEnabled
            ? { billing: { enabled: true } }
            : {};
      const csvExport =
        props.org.features?.csvExport?.signals && state.csvExport
          ? props.org.features.csvExport
          : !props.org.features?.csvExport?.signals && state.csvExport
            ? props.currentOrg.features?.csvExport
            : {};
      const maintenance_v1 = { enabled: state.maintenance_v1 },
        maintenance_v2 = { enabled: state.maintenance_v2 };
      const reqBody = {
        ...state.org,
        features: {
          ...props.org.features,
          apps,
          ...billing,
          csvExport,
          cumminsenabled: state.cumminsenabled,
          maintenance_v1,
          maintenance_v2,
          parts: state.parts,
          remote: state.remote,
        },
      };
      const update = await accountService.updateOrgDetails(
        props.org._id,
        reqBody
      );

      if (props.isSubOrgEdit) return props.handleSubOrgEdit(update);

      props.updateOrg(update);
      props.closeModal();
    } catch (err) {
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        updateRequestPending: false,
      }));
    }
  };

  const toggleView = (viewing) => {
    if (state.viewing === viewing) return;
    updateState((prevState) => ({ ...prevState, viewing }));
  };

  return (
    <Modal
      isOpen={props.isOpen}
      toggle={props.toggleModal}
      centered
      size="lg"
      className={modalClass}
      contentClassName={contentClass}
    >
      <ModalHeader toggle={props.toggleModal}>
        <span className="font-weight-light text-text-light-gray">
          Organization Settings
        </span>
      </ModalHeader>
      <ModalBody className={modalBodyClass}>
        <div className="container-fluid">
          <div className="row">
            <div className="d-none d-md-block col-3 bg-background-gray pt-3 pl-2 modal-side-nav-container">
              <div className="mb-3">
                {state.viewing === 'details' && (
                  <div className="page-indicator position-absolute" />
                )}
                <button
                  className={`btn btn-link view-link text-nowrap p-0 w-100 font-weight-medium-2 text-left text-decoration-none ${
                    state.viewing === 'details' && 'text-muted'
                  }`}
                  onClick={() => toggleView('details')}
                >
                  Org Details
                </button>
              </div>
              <div className="mb-3">
                {state.viewing === 'branding' && (
                  <div className="page-indicator position-absolute" />
                )}
                <button
                  className={`btn btn-link view-link text-nowrap p-0 w-100 font-weight-medium-2 text-left text-decoration-none ${
                    state.viewing === 'branding' && 'text-muted'
                  }`}
                  onClick={() => toggleView('branding')}
                >
                  Branding
                </button>
              </div>
              <div className="mb-3">
                {state.viewing === 'billing' && (
                  <div className="page-indicator position-absolute" />
                )}
                <button
                  className={`btn btn-link view-link text-nowrap p-0 w-100 font-weight-medium-2 text-left text-decoration-none ${
                    state.viewing === 'billing' && 'text-muted'
                  }`}
                  onClick={() => toggleView('billing')}
                >
                  Billing Info
                </button>
              </div>
              {props.isSubOrgEdit &&
              hasWriteAccessToResource(state.org?._id, ORGANIZATION) &&
              featuresAvailable ? (
                <div className="mb-3">
                  {state.viewing === 'features' && (
                    <div className="page-indicator position-absolute" />
                  )}
                  <button
                    className={`btn btn-link view-link text-nowrap p-0 w-100 font-weight-medium-2 text-left text-decoration-none ${
                      state.viewing === 'features' && 'text-muted'
                    }`}
                    onClick={() => toggleView('features')}
                  >
                    Features
                  </button>
                </div>
              ) : null}
            </div>
            <div className="col-12 col-md-9 bg-white container edit-org-content-container">
              <form onSubmit={submitUpdate}>
                {(() => {
                  switch (state.viewing) {
                    case 'details':
                      return (
                        <OrgContactInfo
                          org={state.org}
                          handlePropUpdate={handlePropUpdate}
                          handleSettingsUpdate={handleSettingsUpdate}
                          handlePhoneNumberUpdate={handlePhoneNumberUpdate}
                        />
                      );
                    case 'branding':
                      return <OrgBranding org={state.org} onDrop={onDrop} />;
                    case 'billing':
                      return (
                        <OrgBilling
                          handlePropUpdate={handlePropUpdate}
                          org={state.org}
                        />
                      );
                    case 'features':
                      return (
                        <OrgFeatures
                          billingEnabled={state.billingEnabled}
                          csvExport={state.csvExport}
                          cumminsenabled={state.cumminsenabled}
                          handleFeaturesUpdate={handleFeaturesUpdate}
                          hasBilling={hasBilling}
                          hasCsvReports={hasCsvReports}
                          hasCumminsenabled={hasCumminsenabled}
                          hasMaintenanceV1={hasMaintenanceV1}
                          hasMaintenanceV2={hasMaintenanceV1}
                          hasParts={hasParts}
                          hasRemote={hasRemote}
                          maintenance_v1={state.maintenance_v1}
                          maintenance_v2={state.maintenance_v2}
                          orgHasBillingEnabled={
                            props.org?.features?.billing?.enabled
                          }
                          parts={state.parts}
                          remote={state.remote}
                        />
                      );
                    default:
                      return null;
                  }
                })()}
                <div className="row form-buttons-container">
                  <div className="col">
                    {state.error && (
                      <span className="float-left text-danger col-12 col-md-6">
                        {state.error}
                      </span>
                    )}
                    <button
                      className="btn btn-sm modal-button-submit p-0 float-right save-button"
                      type="submit"
                    >
                      {state.updateRequestPending ? (
                        <FontAwesomeIcon icon={faCircleNotch} spin size="lg" />
                      ) : (
                        'Save'
                      )}
                    </button>
                    <button
                      className="btn btn-small btn-outline-secondary p-0 float-right cancel-button"
                      onClick={props.toggleModal}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </ModalBody>
      <div className="d-flex align-items-center justify-content-around d-md-none d-lg-none d-xl-none w-100 bg-white border-top">
        <button
          className="btn btn-link d-flex flex-column align-items-center justify-content-center"
          onClick={() => toggleView('details')}
        >
          <div>
            <span className="fa-layers fa-fw">
              <FontAwesomeIcon
                className={`text-${
                  state.viewing === 'details'
                    ? 'text-brand-primary'
                    : 'secondary-gray'
                }`}
                icon={faCircle}
                size="lg"
              />
              <FontAwesomeIcon icon={faInfo} color="white" />
            </span>
          </div>
          <div className="mt-1">
            <span
              className={`mobile-link-text text-nowrap ${
                state.viewing === 'details'
                  ? 'text-brand-primary'
                  : 'text-icon-link-gray'
              }`}
            >
              Basics
            </span>
          </div>
        </button>
        <button
          className="btn btn-link d-flex flex-column align-items-center justify-content-center"
          onClick={() => toggleView('branding')}
        >
          <div>
            <span className="fa-layers fa-fw">
              <FontAwesomeIcon
                className={`${
                  state.viewing === 'branding'
                    ? 'text-brand-primary'
                    : 'text-secondary-gray'
                }`}
                icon={faTag}
              />
            </span>
          </div>
          <div className="mt-1">
            <span
              className={`mobile-link-text text-nowrap ${
                state.viewing === 'branding'
                  ? 'text-brand-primary'
                  : 'text-icon-link-gray'
              }
              `}
            >
              Branding
            </span>
          </div>
        </button>
        <button
          className="btn btn-link d-flex flex-column align-items-center justify-content-center"
          onClick={() => toggleView('billing')}
        >
          <div>
            <span className="fa-layers fa-fw">
              <FontAwesomeIcon
                className={`text-${
                  state.viewing === 'billing'
                    ? 'text-brand-primary'
                    : 'text-secondary-gray'
                }`}
                icon={faFileInvoice}
                size="lg"
              />
              <FontAwesomeIcon icon={faInfo} color="white" />
            </span>
          </div>
          <div className="mt-1">
            <span
              className={`mobile-link-text text-nowrap ${
                state.viewing === 'billing'
                  ? 'text-brand-primary'
                  : 'text-icon-link-gray'
              }`}
            >
              Billing
            </span>
          </div>
        </button>
      </div>
      {modalBodyStyles}
      {modalStyles}
      {contentStyles}
      <style jsx>{`
        .btn-link {
          text-decoration: none;
        }

        .page-indicator {
          left: 0;
          height: 20px;
          width: 5px;
          background-color: var(--brand-primary);
          color: var(--brand-primary);
        }

        .view-link {
          font-size: 0.5rem;
          color: var(--icon-link-gray);
        }
        .view-link:hover {
          color: var(--text-muted);
        }

        .edit-org-content-container {
          padding: 1rem;
        }
        .btn {
          width: 80px;
        }
        .cancel-button {
          margin-right: 0.5rem;
        }
        .mobile-link-text {
          font-size: 0.75rem;
          font-weight: 400;
        }
        .form-buttons-container {
          margin-top: 1rem;
        }

        side-nav {
          color: var(--icon-link-gray);
        }
        side-nav:hover {
          color: var(--text-muted);
        }

        @media (min-width: 768px) {
          .modal-side-nav-container {
            height: calc(100% - 64px);
          }
          .page-indicator {
            left: 0;
            height: 20px;
            width: 5px;
            background-color: var(--brand-primary);
            color: var(--brand-primary);
          }

          .view-link {
            font-size: 1rem;
            margin-left: 0.25rem;
          }

          .edit-org-content-container {
            padding: 2rem;
          }
          .btn {
            width: 100px;
          }
          .cancel-button {
            margin-right: 2rem;
          }
          .form-buttons-container {
            margin-top: 30rem;
          }
        }
        @media (min-width: 992px) {
          .modal-side-nav-container {
            height: auto;
          }
          .view-link {
            font-size: 1rem;
            margin-left: 0.5rem;
          }

          .form-buttons-container {
            margin-top: 1rem;
          }
        }
      `}</style>
    </Modal>
  );
}

EditOrgModal.propTypes = {
  closeModal: PropTypes.func,
  currentOrg: PropTypes.object,
  handleSubOrgEdit: PropTypes.func,
  isOpen: PropTypes.bool,
  isSubOrgEdit: PropTypes.bool,
  org: PropTypes.object,
  toggleModal: PropTypes.func,
  updateOrg: PropTypes.func,
};
